// @ts-nocheck
// Location: src/components/admin/analytics/BuzzAgentGameAnalytics.tsx
// Purpose: Buzz Agent Game analytics dashboard with leaderboards and metrics
// Scope: Display agent performance, engagement trends, and game leaderboards

import React, { useState, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { cn } from '@/lib/utils';
import {
	Trophy,
	TrendingUp,
	TrendingDown,
	Eye,
	Heart,
	Users,
	Zap,
	Calendar,
	ChevronUp,
	ChevronDown,
	Award,
	Flame,
	Target,
	Activity,
	ArrowUp,
	ArrowDown,
	Minus,
	ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface BuzzAgentGameAnalyticsProps {
	className?: string;
}

// Metric type for leaderboards
type MetricType = 'views' | 'likes' | 'followers' | 'engagement';
type PeriodType = 'daily' | 'weekly' | 'monthly' | 'all_time';

// Helper component for metric cards
function MetricCard({
	title,
	value,
	change,
	icon: Icon,
	color,
	trend,
}: {
	title: string;
	value: string | number;
	change?: number;
	icon: React.ComponentType<{ className?: string }>;
	color: string;
	trend?: 'up' | 'down' | 'neutral';
}) {
	return (
		<Card className={cn('relative overflow-hidden', color)}>
			<CardContent className='p-6'>
				<div className='flex items-center justify-between'>
					<div>
						<p className='text-sm text-muted-foreground'>{title}</p>
						<p className='text-2xl font-bold mt-1'>{value}</p>
						{change !== undefined && (
							<div className='flex items-center gap-1 mt-2'>
								{trend === 'up' ? (
									<ArrowUp className='h-4 w-4 text-green-500' />
								) : trend === 'down' ? (
									<ArrowDown className='h-4 w-4 text-red-500' />
								) : (
									<Minus className='h-4 w-4 text-gray-500' />
								)}
								<span
									className={cn(
										'text-sm font-medium',
										trend === 'up'
											? 'text-green-500'
											: trend === 'down'
												? 'text-red-500'
												: 'text-gray-500'
									)}
								>
									{Math.abs(change).toFixed(1)}%
								</span>
							</div>
						)}
					</div>
					<div className='p-3 bg-white/20 backdrop-blur-sm rounded-xl'>
						<Icon className='h-6 w-6 text-white' />
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

// Leaderboard row component
function LeaderboardRow({
	rank,
	agent,
	value,
	change,
	metric,
	isCurrentUser,
}: {
	rank: number;
	agent: {
		name: string;
		userEmail?: string;
		agentId: string;
		userId: string;
	};
	value: number;
	change?: number;
	metric: MetricType;
	isCurrentUser?: boolean;
}) {
	const getRankIcon = () => {
		if (rank === 1) return <Trophy className='h-5 w-5 text-yellow-500' />;
		if (rank === 2) return <Award className='h-5 w-5 text-gray-400' />;
		if (rank === 3) return <Award className='h-5 w-5 text-orange-600' />;
		return (
			<span className='text-sm font-bold text-muted-foreground'>
				#{rank}
			</span>
		);
	};

	const formatValue = () => {
		if (metric === 'engagement') {
			return `${value.toFixed(2)}%`;
		}
		if (value >= 1000000) {
			return `${(value / 1000000).toFixed(1)}M`;
		}
		if (value >= 1000) {
			return `${(value / 1000).toFixed(1)}K`;
		}
		return value.toString();
	};

	return (
		<div
			className={cn(
				'flex items-center justify-between p-4 rounded-lg transition-all hover:shadow-md',
				isCurrentUser
					? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30'
					: 'bg-white/50 dark:bg-gray-900/50 border border-gray-200/50 dark:border-gray-800/30'
			)}
		>
			<div className='flex items-center gap-4'>
				<div className='w-10 flex justify-center'>{getRankIcon()}</div>
				<div>
					<div className='font-medium text-gray-900 dark:text-white'>
						{agent.name}
					</div>
					<div className='text-sm text-muted-foreground'>
						@{agent.userEmail?.split('@')[0] || 'unknown'}
					</div>
				</div>
			</div>
			<div className='flex items-center gap-4'>
				{change !== undefined && change !== 0 && (
					<div className='flex items-center gap-1'>
						{change > 0 ? (
							<ChevronUp className='h-4 w-4 text-green-500' />
						) : (
							<ChevronDown className='h-4 w-4 text-red-500' />
						)}
						<span
							className={cn(
								'text-sm font-medium',
								change > 0 ? 'text-green-500' : 'text-red-500'
							)}
						>
							{Math.abs(change)}
						</span>
					</div>
				)}
				<div className='text-right'>
					<div className='font-bold text-lg'>{formatValue()}</div>
					<div className='text-xs text-muted-foreground capitalize'>
						{metric === 'engagement' ? 'Rate' : metric}
					</div>
				</div>
			</div>
		</div>
	);
}

export function BuzzAgentGameAnalytics({
	className,
}: BuzzAgentGameAnalyticsProps) {
	const { user, isAdmin } = useAuth();
	const navigate = useNavigate();
	const [selectedMetric, setSelectedMetric] =
		useState<MetricType>('engagement');
	const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('weekly');
	const [leaderboardTab, setLeaderboardTab] = useState<'top' | 'movers'>(
		'top'
	);

	// Query current game period
	const currentGamePeriod = useQuery(
		api.buzzAgentGamePeriods.getCurrentGamePeriod
	);

	// Query leaderboard data
	const leaderboard = useQuery(api.buzzAgentLeaderboards.getLeaderboard, {
		period: selectedPeriod,
		metric: selectedMetric,
		limit: 20,
	});

	// Query top movers (both up and down)
	const topMoversUp = useQuery(api.buzzAgentLeaderboards.getTopMovers, {
		period: selectedPeriod === 'all_time' ? 'weekly' : selectedPeriod,
		metric: selectedMetric,
		direction: 'up',
		limit: 5,
	});

	const topMoversDown = useQuery(api.buzzAgentLeaderboards.getTopMovers, {
		period: selectedPeriod === 'all_time' ? 'weekly' : selectedPeriod,
		metric: selectedMetric,
		direction: 'down',
		limit: 5,
	});

	// Query user's agents rankings
	const userAgentsRankings = useQuery(
		api.buzzAgentLeaderboards.getUserAgentsRankings,
		user
			? {
					userId: user._id as unknown as Id<'users'>,
					period: selectedPeriod,
					paginationOpts: {
						numItems: 10,
						cursor: null,
					},
				}
			: 'skip'
	);

	// Query overall game stats
	const gameStats = useQuery(api.buzzAgentGameStats.getGameSummaryStats, {
		period: selectedPeriod === 'all_time' ? 'all' : selectedPeriod,
	});

	// Calculate summary metrics
	const summaryMetrics = useMemo(() => {
		if (!gameStats) return null;

		return {
			totalAgents: gameStats.totalAgents || 0,
			activeAgents: gameStats.activeAgents || 0,
			totalViews: gameStats.totalViews || 0,
			totalLikes: gameStats.totalLikes || 0,
			avgEngagement: gameStats.avgEngagementRate || 0,
			topPerformer: gameStats.topAgent || null,
		};
	}, [gameStats]);

	if (!user || !isAdmin) {
		return (
			<div className='container mx-auto p-6'>
				<Card className='mt-6'>
					<CardContent>
						<div className='flex flex-col items-center justify-center space-y-6 p-8'>
							<h2 className='text-2xl font-bold text-red-500'>
								Access Denied
							</h2>
							<p className='text-lg text-gray-400 text-center'>
								You do not have permission to access Buzz Agent
								Game Analytics.
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Check if there's an active game period
	if (
		!currentGamePeriod ||
		!['active', 'recruiting', 'setup'].includes(currentGamePeriod.status)
	) {
		return (
			<div className='container mx-auto p-6'>
				<Card className='mt-6'>
					<CardContent>
						<div className='flex flex-col items-center justify-center space-y-6 p-8'>
							<Zap className='h-16 w-16 text-purple-500/30' />
							<h2 className='text-2xl font-bold text-gray-300'>
								No Active Game Period
							</h2>
							<p className='text-lg text-gray-400 text-center'>
								The Buzz Agent Game is not currently active.
								{isAdmin && (
									<span className='block mt-2'>
										Please start a game period from the
										Admin Dashboard.
									</span>
								)}
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className={cn('space-y-6 my-20', className)}>
			{/* Header */}
			<div>
				<Button
					variant='ghost'
					size='sm'
					onClick={() => navigate('/admin/analytics')}
					className='hover:bg-yellow-500/10'
				>
					<ArrowLeft className='h-4 w-4 mr-2' />
					<span>Back to Analytics</span>
				</Button>
			</div>
			<div className='flex items-center justify-between'>
				<div>
					<h1 className='text-3xl font-bold text-white flex items-center gap-3'>
						<Zap className='h-8 w-8 text-purple-500' />
						Buzz Agent Game Analytics
					</h1>
					<p className='text-muted-foreground mt-2'>
						{currentGamePeriod ? (
							<>
								{currentGamePeriod.name} -{' '}
								{currentGamePeriod.description}
							</>
						) : (
							'Track agent performance, engagement metrics, and game leaderboards'
						)}
					</p>
				</div>
				{currentGamePeriod && (
					<Badge
						variant='outline'
						className={cn(
							'px-4 py-2 border-0',
							currentGamePeriod.status === 'active'
								? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
								: currentGamePeriod.status === 'recruiting'
									? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
									: currentGamePeriod.status === 'ended'
										? 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
										: 'bg-gray-600 text-white'
						)}
					>
						<Flame className='h-4 w-4 mr-2' />
						{currentGamePeriod.status === 'active'
							? 'Game Active'
							: currentGamePeriod.status === 'recruiting'
								? 'Recruiting'
								: currentGamePeriod.status === 'ended'
									? 'Game Ended'
									: currentGamePeriod.status === 'setup'
										? 'Setup Phase'
										: 'Game Paused'}
					</Badge>
				)}
			</div>

			{/* Summary Metrics */}
			{summaryMetrics ? (
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4'>
					<MetricCard
						title='Active Agents'
						value={summaryMetrics.activeAgents}
						icon={Zap}
						color='bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20'
						trend='up'
						change={12.5}
					/>
					<MetricCard
						title='Total Views'
						value={
							summaryMetrics.totalViews >= 1000000
								? `${(summaryMetrics.totalViews / 1000000).toFixed(1)}M`
								: summaryMetrics.totalViews >= 1000
									? `${(summaryMetrics.totalViews / 1000).toFixed(1)}K`
									: summaryMetrics.totalViews
						}
						icon={Eye}
						color='bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-500/20'
						trend='up'
						change={23.7}
					/>
					<MetricCard
						title='Total Likes'
						value={
							summaryMetrics.totalLikes >= 1000000
								? `${(summaryMetrics.totalLikes / 1000000).toFixed(1)}M`
								: summaryMetrics.totalLikes >= 1000
									? `${(summaryMetrics.totalLikes / 1000).toFixed(1)}K`
									: summaryMetrics.totalLikes
						}
						icon={Heart}
						color='bg-gradient-to-br from-pink-500/10 to-red-500/10 border-pink-500/20'
						trend='up'
						change={18.3}
					/>
					<MetricCard
						title='Avg Engagement'
						value={`${summaryMetrics.avgEngagement.toFixed(2)}%`}
						icon={Activity}
						color='bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20'
						trend='neutral'
						change={0.5}
					/>
					<MetricCard
						title='Top Performer'
						value={summaryMetrics.topPerformer?.name || 'N/A'}
						icon={Trophy}
						color='bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20'
					/>
				</div>
			) : (
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4'>
					{[...Array(5)].map((_, i) => (
						<Skeleton key={i} className='h-32' />
					))}
				</div>
			)}

			{/* Period and Metric Selectors */}
			<Card>
				<CardHeader>
					<CardTitle>Leaderboard Controls</CardTitle>
				</CardHeader>
				<CardContent>
					<div className='flex flex-col lg:flex-row gap-4'>
						{/* Period Selector */}
						<div className='flex-1'>
							<label className='text-sm font-medium text-muted-foreground mb-2 block'>
								Time Period
							</label>
							<div className='flex gap-2'>
								{(
									[
										'daily',
										'weekly',
										'monthly',
										'all_time',
									] as PeriodType[]
								).map((period) => (
									<Button
										key={period}
										variant={
											selectedPeriod === period
												? 'default'
												: 'outline'
										}
										size='sm'
										onClick={() =>
											setSelectedPeriod(period)
										}
										className={cn(
											'flex-1 capitalize',
											selectedPeriod === period &&
												'bg-gradient-to-r from-purple-500 to-pink-600 text-white border-0'
										)}
									>
										<Calendar className='h-4 w-4 mr-1' />
										{period === 'all_time'
											? 'All Time'
											: period.replace('_', ' ')}
									</Button>
								))}
							</div>
						</div>

						{/* Metric Selector */}
						<div className='flex-1'>
							<label className='text-sm font-medium text-muted-foreground mb-2 block'>
								Metric
							</label>
							<div className='flex gap-2'>
								{(
									[
										'engagement',
										'views',
										'likes',
										'followers',
									] as MetricType[]
								).map((metric) => (
									<Button
										key={metric}
										variant={
											selectedMetric === metric
												? 'default'
												: 'outline'
										}
										size='sm'
										onClick={() =>
											setSelectedMetric(metric)
										}
										className={cn(
											'flex-1 capitalize',
											selectedMetric === metric &&
												'bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-0'
										)}
									>
										{metric === 'engagement' && (
											<Activity className='h-4 w-4 mr-1' />
										)}
										{metric === 'views' && (
											<Eye className='h-4 w-4 mr-1' />
										)}
										{metric === 'likes' && (
											<Heart className='h-4 w-4 mr-1' />
										)}
										{metric === 'followers' && (
											<Users className='h-4 w-4 mr-1' />
										)}
										{metric}
									</Button>
								))}
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Leaderboard */}
			<Card>
				<CardHeader>
					<CardTitle className='flex items-center gap-2'>
						<Trophy className='h-5 w-5 text-yellow-500' />
						Buzz Agent Leaderboard
					</CardTitle>
				</CardHeader>
				<CardContent>
					<Tabs
						value={leaderboardTab}
						onValueChange={(v) =>
							setLeaderboardTab(v as 'top' | 'movers')
						}
					>
						<TabsList className='grid w-full grid-cols-2'>
							<TabsTrigger value='top'>Top Agents</TabsTrigger>
							<TabsTrigger value='movers'>Top Movers</TabsTrigger>
						</TabsList>

						<TabsContent value='top' className='space-y-3 mt-6'>
							{leaderboard ? (
								leaderboard.length > 0 ? (
									leaderboard.map(
										(entry: {
											agentId: string;
											rank: number;
											value: number;
											previousRank?: number;
											userId: string;
											name: string;
											userEmail?: string;
										}) => (
											<LeaderboardRow
												key={entry.agentId}
												rank={entry.rank}
												agent={entry}
												value={entry.value}
												change={
													entry.previousRank
														? entry.previousRank -
															entry.rank
														: undefined
												}
												metric={selectedMetric}
												isCurrentUser={
													(entry.userId as string) ===
													(user._id as unknown as string)
												}
											/>
										)
									)
								) : (
									<div className='text-center py-12 text-muted-foreground'>
										<Trophy className='h-12 w-12 mx-auto mb-4 text-gray-400' />
										<p className='text-lg'>
											No agents found for this period
										</p>
										<p className='text-sm mt-2'>
											Create Buzz Agents to start
											competing!
										</p>
									</div>
								)
							) : (
								<div className='space-y-3'>
									{[...Array(5)].map((_, i) => (
										<Skeleton key={i} className='h-20' />
									))}
								</div>
							)}
						</TabsContent>

						<TabsContent value='movers' className='space-y-3 mt-6'>
							{topMoversUp !== undefined ||
							topMoversDown !== undefined ? (
								<div className='space-y-6'>
									{/* Top Gainers */}
									<div>
										<h3 className='text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2'>
											<TrendingUp className='h-4 w-4 text-green-500' />
											Biggest Improvements
										</h3>
										{topMoversUp &&
										topMoversUp.length > 0 ? (
											topMoversUp.map(
												(entry: {
													agentId: string;
													rank: number;
													value: number;
													changePercent: number;
													userId: string;
													name: string;
													userEmail?: string;
												}) => (
													<LeaderboardRow
														key={entry.agentId}
														rank={entry.rank}
														agent={entry}
														value={entry.value}
														change={
															entry.changePercent
														}
														metric={selectedMetric}
														isCurrentUser={
															entry.userId ===
															(user._id as unknown as string)
														}
													/>
												)
											)
										) : (
											<p className='text-sm text-muted-foreground py-4'>
												No improving agents this period
											</p>
										)}
									</div>

									{/* Top Losers */}
									<div>
										<h3 className='text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2'>
											<TrendingDown className='h-4 w-4 text-red-500' />
											Biggest Declines
										</h3>
										{topMoversDown &&
										topMoversDown.length > 0 ? (
											topMoversDown.map(
												(entry: {
													agentId: string;
													rank: number;
													value: number;
													changePercent: number;
													userId: string;
													name: string;
													userEmail?: string;
												}) => (
													<LeaderboardRow
														key={entry.agentId}
														rank={entry.rank}
														agent={entry}
														value={entry.value}
														change={
															entry.changePercent
														}
														metric={selectedMetric}
														isCurrentUser={
															entry.userId ===
															(user._id as unknown as string)
														}
													/>
												)
											)
										) : (
											<p className='text-sm text-muted-foreground py-4'>
												No declining agents this period
											</p>
										)}
									</div>
								</div>
							) : (
								<div className='space-y-3'>
									{[...Array(5)].map((_, i) => (
										<Skeleton key={i} className='h-20' />
									))}
								</div>
							)}
						</TabsContent>
					</Tabs>
				</CardContent>
			</Card>

			{/* User's Agents Performance */}
			{userAgentsRankings &&
				userAgentsRankings.page &&
				userAgentsRankings.page.length > 0 && (
					<Card>
						<CardHeader>
							<CardTitle className='flex items-center gap-2'>
								<Target className='h-5 w-5 text-purple-500' />
								Your Agents Performance
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className='space-y-3'>
								{userAgentsRankings.page.map(
									(agent: {
										agentId: Id<'buzzAgents'>;
										agentName: string;
										rankings: {
											views?: {
												rank: number;
												value: number;
											};
											likes?: {
												rank: number;
												value: number;
											};
											followers?: {
												rank: number;
												value: number;
											};
											engagement?: {
												rank: number;
												value: number;
											};
										};
									}) => (
										<div
											key={agent.agentId}
											className='p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20'
										>
											<div className='flex items-center justify-between mb-3'>
												<div>
													<h4 className='font-medium'>
														{agent.agentName}
													</h4>
													<p className='text-sm text-muted-foreground'>
														Agent Performance
													</p>
												</div>
												<Badge
													variant='outline'
													className='bg-purple-500/10'
												>
													Rank #
													{agent.rankings?.engagement
														?.rank || 'N/A'}
												</Badge>
											</div>
											<div className='grid grid-cols-4 gap-4 text-center'>
												<div>
													<p className='text-xs text-muted-foreground'>
														Views
													</p>
													<p className='font-bold'>
														{agent.rankings?.views
															?.value || 0}
													</p>
													{agent.rankings?.views
														?.rank && (
														<p className='text-xs text-purple-500'>
															#
															{
																agent.rankings
																	.views.rank
															}
														</p>
													)}
												</div>
												<div>
													<p className='text-xs text-muted-foreground'>
														Likes
													</p>
													<p className='font-bold'>
														{agent.rankings?.likes
															?.value || 0}
													</p>
													{agent.rankings?.likes
														?.rank && (
														<p className='text-xs text-purple-500'>
															#
															{
																agent.rankings
																	.likes.rank
															}
														</p>
													)}
												</div>
												<div>
													<p className='text-xs text-muted-foreground'>
														Followers
													</p>
													<p className='font-bold'>
														{agent.rankings
															?.followers
															?.value || 0}
													</p>
													{agent.rankings?.followers
														?.rank && (
														<p className='text-xs text-purple-500'>
															#
															{
																agent.rankings
																	.followers
																	.rank
															}
														</p>
													)}
												</div>
												<div>
													<p className='text-xs text-muted-foreground'>
														Engagement
													</p>
													<p className='font-bold'>
														{agent.rankings
															?.engagement?.value
															? `${agent.rankings.engagement.value.toFixed(2)}%`
															: '0%'}
													</p>
													{agent.rankings?.engagement
														?.rank && (
														<p className='text-xs text-purple-500'>
															#
															{
																agent.rankings
																	.engagement
																	.rank
															}
														</p>
													)}
												</div>
											</div>
										</div>
									)
								)}
							</div>
						</CardContent>
					</Card>
				)}

			{/* Game Info */}
			<Card className='bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20'>
				<CardContent className='p-6'>
					<div className='flex items-start gap-4'>
						<div className='p-3 bg-purple-500/10 rounded-xl'>
							<Zap className='h-6 w-6 text-purple-500' />
						</div>
						<div className='flex-1'>
							<h3 className='font-semibold mb-2'>
								{currentGamePeriod
									? `${currentGamePeriod.name}`
									: 'About Buzz Agent Game'}
							</h3>
							<p className='text-sm text-muted-foreground mb-3'>
								Compete with your Buzz Agents to climb the
								leaderboards! Agents earn points based on
								engagement metrics including views, likes, and
								follower growth. The more engaging your agent's
								content, the higher you'll rank!
							</p>
							<div className='grid grid-cols-2 md:grid-cols-4 gap-4 text-sm'>
								{currentGamePeriod && (
									<>
										<div>
											<span className='text-muted-foreground'>
												Game Period:
											</span>
											<span className='ml-2 font-medium'>
												{currentGamePeriod.type}
											</span>
										</div>
										{currentGamePeriod.startDate && (
											<div>
												<span className='text-muted-foreground'>
													Started:
												</span>
												<span className='ml-2 font-medium'>
													{new Date(
														currentGamePeriod.startDate
													).toLocaleDateString()}
												</span>
											</div>
										)}
										{currentGamePeriod.endDate && (
											<div>
												<span className='text-muted-foreground'>
													Ends:
												</span>
												<span className='ml-2 font-medium'>
													{new Date(
														currentGamePeriod.endDate
													).toLocaleDateString()}
												</span>
											</div>
										)}
										{currentGamePeriod.prizes && (
											<div>
												<span className='text-muted-foreground'>
													Prize Pool:
												</span>
												<span className='ml-2 font-medium text-purple-500'>
													{(currentGamePeriod.prizes
														.first || 0) +
														(currentGamePeriod
															.prizes.second ||
															0) +
														(currentGamePeriod
															.prizes.third ||
															0)}{' '}
													KARMA
												</span>
											</div>
										)}
									</>
								)}
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
